import { createContext, useContext, useState, useEffect, useMemo } from "react";
import { ThemeProvider as MuiThemeProvider } from "@mui/material/styles";
import { createLightTheme, createDarkTheme } from "../styles/MuiTheme";

const ThemeContext = createContext({
  mode: "light",
  toggleTheme: () => {},
  isDark: false,
});

export const useThemeMode = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useThemeMode must be used within ThemeContextProvider");
  }
  return context;
};

export function ThemeContextProvider({ children }) {
  // Initialize from localStorage or system preference
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem("theme-mode");
    if (savedMode) return savedMode;
    
    // Check system preference
    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) {
      return "dark";
    }
    return "light";
  });

  // Listen for system theme changes
  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handleChange = (e) => {
      const savedMode = localStorage.getItem("theme-mode");
      if (!savedMode) {
        setMode(e.matches ? "dark" : "light");
      }
    };

    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  // Save to localStorage when mode changes
  useEffect(() => {
    localStorage.setItem("theme-mode", mode);
    
    // Update document body class for global CSS access
    document.body.classList.remove("light-mode", "dark-mode");
    document.body.classList.add(`${mode}-mode`);
    
    // Update meta theme-color for mobile browsers
    const metaThemeColor = document.querySelector('meta[name="theme-color"]');
    if (metaThemeColor) {
      metaThemeColor.setAttribute("content", mode === "dark" ? "#021e2d" : "#ffffff");
    }
  }, [mode]);

  const toggleTheme = () => {
    setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
  };

  const theme = useMemo(() => {
    return mode === "dark" ? createDarkTheme() : createLightTheme();
  }, [mode]);

  const contextValue = useMemo(() => ({
    mode,
    toggleTheme,
    isDark: mode === "dark",
  }), [mode]);

  return (
    <ThemeContext.Provider value={contextValue}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export default ThemeContext;
